import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
// Loads the same device settings data used by Device Settings.
import { UseDeviceSettings } from "../../services/API/ControllerAPI/GetDevicesSettings.jsx";

import {
  useAlertData,
  useExpiringSettings,
} from "../../services/API/GetAlerts";

import { useAllShopsData } from "../../services/API/ShopAPI/GetAllShopDetails.jsx";

import { getAllShopControllers } from "../../services/API/ShopAPI/GetShopControllers.jsx";

import { SETTINGS_TYPE_MAP } from "../constants.js";

// Keeps upcoming alerts visible for the next 48 hours.
const UPCOMING_ALERT_MAX_HOURS = 48;

const ONE_HOUR_MS = 60 * 60 * 1000;
// Keeps expired alerts visible for the last 30 days.
const EXPIRED_ALERT_MAX_AGE_DAYS = 30;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Calculates the expiration date using the remaining hours provided by the API.
const getExpiryDateFromRemainingHours = (remHours) => {
  const hours = Number(remHours);

  // Do not calculate a new expiration date for expired settings.
  if (!Number.isFinite(hours) || hours <= 0) {
    return null;
  }

  return new Date(Date.now() + hours * ONE_HOUR_MS);
};

// Converts a DD/MM/YYYY expiration date returned by the API into a Date object.
const parseExpiryDate = (expiryDateText) => {
  if (!expiryDateText) {
    return null;
  }

  const [day, month, year] = String(expiryDateText).split("/").map(Number);

  if (!day || !month || !year) {
    return null;
  }

  const expiryDate = new Date(year, month - 1, day);

  // Treats the setting as active until the end of its expiration day.
  expiryDate.setHours(23, 59, 59, 999);

  return Number.isNaN(expiryDate.getTime()) ? null : expiryDate;
};

// Resolves the expiration date using the same logic as Device Settings.
const getSettingExpiryDate = (setting) => {
  // Uses the expiration date already calculated by Device Settings first.
  const fixedExpiryDate = parseExpiryDate(setting?.expiryDate);

  if (fixedExpiryDate) {
    return fixedExpiryDate;
  }

  // Falls back to the remaining hours when no fixed date is available.
  return getExpiryDateFromRemainingHours(setting?.rem_hours);
};

// Keeps only settings that are allowed to appear in expiration alerts.
const ALERT_SETTING_TYPES = SETTINGS_TYPE_MAP.filter((settingType) =>
  [
    "Security Mode",
    "One Door Only",
    "Intrusion Alarm",
    "2-Way Authentication",
    "Opening Delay",
  ].includes(settingType.name),
);

// Builds the shared expiration alerts used by both the top bar and alerts popup.
export const useExpirationAlerts = () => {
  // Loads notification data used to resolve device information.
  const { data: alertData, isPending, error } = useAlertData();

  // Loads settings that are expired or will expire soon.
  const { data: expiringSettingsData } = useExpiringSettings();

  // Loads all registered shops.
  const { data: allShopsData } = useAllShopsData();

  // Normalizes the expiring settings response into a safe array.
  const expiringSettings = useMemo(() => {
    return Array.isArray(expiringSettingsData) ? expiringSettingsData : [];
  }, [expiringSettingsData]);

  // Extracts all valid shop IDs returned by the shops API.
  const shopIds = useMemo(() => {
    if (!Array.isArray(allShopsData)) {
      return [];
    }

    return allShopsData.map((shop) => shop?.id).filter(Boolean);
  }, [allShopsData]);

  // Loads controllers from all registered shops.
  const { data: allShopControllerData = [] } = useQuery({
    queryKey: ["AllShopControllers", shopIds],

    queryFn: () => getAllShopControllers(shopIds),

    enabled: shopIds.length > 0,

    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,

    refetchOnWindowFocus: false,
    retry: false,
  });

  // Combines controllers returned from all shops into a single list.
  const allControllers = useMemo(() => {
    return allShopControllerData.flatMap((shopData) => {
      const controllers = Array.isArray(shopData?.controllerDetails)
        ? shopData.controllerDetails
        : [];

      return controllers.map((controller) => ({
        ...controller,
        shopId: shopData?.shopId,
      }));
    });
  }, [allShopControllerData]);

  // Extracts all unique device IDs from the registered controllers.
  const alertDeviceIds = useMemo(() => {
    return [
      ...new Set(
        allControllers
          .map((controller) => controller?.id ?? controller?.device_id)
          .filter(Boolean),
      ),
    ];
  }, [allControllers]);

  // Loads the same device settings used by the Device Settings screen.
  const { data: deviceSettingsData = [], refetch: refetchDeviceSettings } =
    UseDeviceSettings(alertDeviceIds, {
      enabled: alertDeviceIds.length > 0,
    });

  // Groups device settings by device ID and setting address.
  const deviceSettingsByKey = useMemo(() => {
    const map = {};

    const devices = Array.isArray(deviceSettingsData) ? deviceSettingsData : [];

    for (const device of devices) {
      const deviceId = device?.deviceId;

      if (!deviceId) {
        continue;
      }

      const settings = Array.isArray(device?.settings) ? device.settings : [];

      if (!map[deviceId]) {
        map[deviceId] = {};
      }

      for (const setting of settings) {
        const key = `${Number(setting?.addr)}:${Number(setting?.bit_pos)}`;

        const previous = map[deviceId][key];
        const previousTime = Number(previous?.sys_time ?? 0);
        const currentTime = Number(setting?.sys_time ?? 0);

        // Keeps the most recent setting for each address and bit position.
        if (!previous || currentTime >= previousTime) {
          map[deviceId][key] = setting;
        }
      }
    }

    return map;
  }, [deviceSettingsData]);

  // Builds the final expiration alerts shared by the top bar and alerts popup.
  const alerts = useMemo(() => {
    if (!alertData) {
      return [];
    }

    return alertDeviceIds
      .flatMap((deviceId, deviceIndex) => {
        // Finds the matching notification device for display information.
        const deviceInfo = Array.isArray(alertData)
          ? alertData.find(
              (device) => (device?.device_id ?? device?.id) === deviceId,
            )
          : null;

        // Finds the controller that matches the current device.
        const controllerInfo = allControllers.find(
          (controller) =>
            String(
              controller?.device_id ?? controller?.id ?? "",
            ).toLowerCase() === String(deviceId ?? "").toLowerCase(),
        );

        // Gets the latest settings for the current device.
        const currentDeviceSettings = Object.values(
          deviceSettingsByKey?.[deviceId] ?? {},
        );

        // Gets upcoming settings from the original expiration API.
        const upcomingDeviceSettings = expiringSettings.filter(
          (setting) =>
            String(setting?.deviceId ?? "").toLowerCase() ===
            String(deviceId ?? "").toLowerCase(),
        );

        // Defines the oldest expiration date allowed in the alerts popup.
        const expiredCutoffDate =
          Date.now() - EXPIRED_ALERT_MAX_AGE_DAYS * ONE_DAY_MS;

        // Keeps only upcoming settings that do not already exist
        // in the current Device Settings data.
        const uniqueUpcomingSettings = upcomingDeviceSettings.filter(
          (upcomingSetting) =>
            !currentDeviceSettings.some(
              (currentSetting) =>
                Number(currentSetting?.addr) ===
                  Number(upcomingSetting?.addr) &&
                Number(currentSetting?.bit_pos) ===
                  Number(upcomingSetting?.bit_pos),
            ),
        );

        // Combines the current Device Settings with non-duplicated upcoming settings.
        const combinedSettings = [
          ...currentDeviceSettings,
          ...uniqueUpcomingSettings,
        ];

        const filteredExpiringSettings = combinedSettings.filter((setting) => {
          const settingType = ALERT_SETTING_TYPES.find(
            (item) =>
              Number(item.addr) === Number(setting?.addr) &&
              Number(item.bit_pos) === Number(setting?.bit_pos),
          );

          if (!settingType) {
            return false;
          }

          // Hides settings that are currently Off.
          if (Number(setting?.V) === 0) {
            return false;
          }

          // Uses the same expiration date logic as Device Settings.
          const expiryDate = getSettingExpiryDate(setting);

          if (!expiryDate) {
            return false;
          }

          const expiryTime = expiryDate.getTime();

          // Keeps settings expired within the last 30 days.
          if (expiryTime < Date.now()) {
            return expiryTime >= expiredCutoffDate;
          }

          // Keeps future settings only within the next 48 hours.
          const upcomingCutoffDate =
            Date.now() + UPCOMING_ALERT_MAX_HOURS * ONE_HOUR_MS;

          return expiryTime <= upcomingCutoffDate;
        });

        return filteredExpiringSettings.map((setting, settingIndex) => {
          // Converts the setting system timestamp into milliseconds.
          const systemTimestamp = Number(setting?.sys_time);

          if (!Number.isFinite(systemTimestamp) || systemTimestamp <= 0) {
            return null;
          }

          const systemTime = new Date(
            systemTimestamp > 100000000000
              ? systemTimestamp
              : systemTimestamp * 1000,
          );

          if (Number.isNaN(systemTime.getTime())) {
            return null;
          }

          // Converts the remaining hours into a numeric value when available.
          const parsedRemainingHours = Number(setting?.rem_hours);

          const remainingHours = Number.isFinite(parsedRemainingHours)
            ? parsedRemainingHours
            : null;

          // Uses the same expiration date logic as Device Settings.
          const expiryDate = getSettingExpiryDate(setting);

          if (!expiryDate) {
            return null;
          }

          // A setting is expired when the controller reports zero remaining hours.
          const isExpired =
            Number.isFinite(remainingHours) && remainingHours === 0;

          // Calculates the remaining hours from the same expiration date
          // used by Device Settings.
          const calculatedRemainingHours =
            (expiryDate.getTime() - Date.now()) / ONE_HOUR_MS;

          // Resolves the display name for the current setting.
          const settingType = SETTINGS_TYPE_MAP.find(
            (item) =>
              Number(item.addr) === Number(setting?.addr) &&
              Number(item.bit_pos) === Number(setting?.bit_pos),
          );

          if (!settingType) {
            return null;
          }

          // Resolves the device display name.
          const deviceName =
            deviceInfo?.device_info?.ssid ??
            controllerInfo?.device_info?.ssid ??
            controllerInfo?.ssid ??
            "-";

          // Finds the matching setting from the original expiration API.
          const originalExpiringSetting = expiringSettings.find(
            (expiringSetting) =>
              String(expiringSetting?.deviceId ?? "").toLowerCase() ===
                String(deviceId ?? "").toLowerCase() &&
              Number(expiringSetting?.addr) === Number(setting?.addr) &&
              Number(expiringSetting?.bit_pos) === Number(setting?.bit_pos),
          );

          // Resolves all shop names linked to the current device.
          // Uses the original expiration API first and falls back to controller shops.
          const shopNamesFromExpiration = Array.isArray(
            originalExpiringSetting?.shopNames,
          )
            ? originalExpiringSetting.shopNames
            : [];

          const shopNamesFromControllers = allControllers
            .filter(
              (controller) =>
                String(
                  controller?.id ?? controller?.device_id ?? "",
                ).toLowerCase() === String(deviceId ?? "").toLowerCase(),
            )
            .map((controller) => {
              const shop = Array.isArray(allShopsData)
                ? allShopsData.find(
                    (item) => String(item?.id) === String(controller?.shopId),
                  )
                : null;

              return shop?.name ?? shop?.shopName ?? shop?.shop_name ?? null;
            })
            .filter(Boolean);

          const shopName = [
            ...new Set([
              ...shopNamesFromExpiration,
              ...shopNamesFromControllers,
            ]),
          ].join(" | ");

          // Hides alerts when required display information is missing.
          if (deviceName === "-" || shopName === "-") {
            return null;
          }

          return {
            id: `${deviceIndex}-${settingIndex}-${deviceId}-${setting.id}-${setting.addr}-${setting.bit_pos}`,
            deviceId: String(deviceId ?? ""),
            deviceName,
            shopName,
            settingName: settingType.name,
            createdAt: systemTime,
            expiryDate,
            remainingHours: calculatedRemainingHours,
            isExpired,
          };
        });
      })
      .filter(Boolean);
  }, [
    alertData,
    alertDeviceIds,
    allControllers,
    expiringSettings,
    deviceSettingsByKey,
  ]);

  // Exposes the shared alerts, request state, and manual refresh.
  return {
    alerts,
    isPending,
    error,
    refetchDeviceSettings,

    // Exposes all device IDs used by the alerts refresh.
    alertDeviceIds,
  };
};
